import { useQuery } from "@tanstack/react-query";
import {
  Building,
  Contact as ContactIcon,
  Handshake,
  Search,
} from "lucide-react";
import { useDataProvider, useTranslate, type Identifier } from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import type { CrmDataProvider } from "../providers/types";
import type { LeadDuplicate, LeadDuplicateInput } from "../types";

// Duplikatsjekken i hurtigregistreringen (kap. 6.5, AK37 og AK39): samme
// oppslag som inntaket (find_lead_duplicates), kjørt mens vi skriver. Panelet
// ser ut som «Kjenner vi dem?» i kommandopaletten.

const DEBOUNCE_MS = 350;

const NO_MATCHES: LeadDuplicate[] = [];

// Et oppslag lønner seg først når feltet kan gi et treff: en e-post med @,
// et telefonnummer med minst 8 siffer, et org.nr på 9 siffer eller et
// firmanavn på minst 3 tegn.
export const duplicateQueryInput = (
  input: LeadDuplicateInput,
): LeadDuplicateInput | null => {
  const email = input.email?.trim().toLowerCase() ?? "";
  const phoneDigits = (input.phone ?? "").replace(/\D/g, "");
  const orgNr = (input.org_nr ?? "").replace(/\s/g, "");
  const company = input.company_name?.trim() ?? "";
  const query: LeadDuplicateInput = {};
  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) query.email = email;
  if (phoneDigits.length >= 8) query.phone = input.phone!.trim();
  if (/^\d{9}$/.test(orgNr)) query.org_nr = orgNr;
  if (company.length >= 3) query.company_name = company;
  if (input.website?.trim()) query.website = input.website.trim();
  return Object.keys(query).length ? query : null;
};

const useDebounced = <T,>(value: T, delay: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

export const useLeadDuplicates = (input: LeadDuplicateInput) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const query = duplicateQueryInput(input);
  const key = JSON.stringify(query);
  const debouncedKey = useDebounced(key, DEBOUNCE_MS);
  const { data, isFetching } = useQuery<LeadDuplicate[]>({
    queryKey: ["find_lead_duplicates", debouncedKey],
    queryFn: () =>
      dataProvider.findLeadDuplicates(JSON.parse(debouncedKey) ?? {}),
    enabled: debouncedKey !== "null",
    staleTime: 30_000,
  });
  return {
    hasInput: query != null,
    // Treffene hører til det vi skrev; mens vi skriver videre, står de til
    // neste svar kommer.
    matches: query == null ? NO_MATCHES : (data ?? NO_MATCHES),
    isPending: query != null && (isFetching || key !== debouncedKey),
  };
};

// Hva treffene betyr for leaden.
export const summarizeDuplicates = (matches: LeadDuplicate[]) => {
  const deals = matches.filter((m) => m.kind === "deal");
  return {
    openDeals: deals.filter((m) => (m.deal_status ?? "open") === "open"),
    lostDeals: deals.filter((m) => m.deal_status === "lost"),
    customer: matches.find((m) => m.kind === "company" && m.is_customer),
    contacts: matches.filter((m) => m.kind === "contact"),
    companies: matches.filter((m) => m.kind === "company"),
  };
};

const MATCH_FALLBACK: Record<LeadDuplicate["match"], string> = {
  email: "e-post",
  phone: "telefon",
  org_nr: "org.nr.",
  domain: "domene",
  name: "navn",
};

export const LeadDuplicatePanel = ({
  matches,
  hasInput,
  isPending,
  onAddNote,
  onUse,
  usedContactId,
  usedCompanyId,
}: {
  matches: LeadDuplicate[];
  hasInput: boolean;
  isPending?: boolean;
  // «Legg til notat i stedet» på en åpen mulighet.
  onAddNote?: (match: LeadDuplicate) => void;
  // Bruk kontakten eller kunden vi fant i stedet for å lage en ny.
  onUse?: (match: LeadDuplicate) => void;
  usedContactId?: Identifier | null;
  usedCompanyId?: Identifier | null;
}) => {
  const translate = useTranslate();
  const summary = useMemo(() => summarizeDuplicates(matches), [matches]);
  const matchLabel = (match: LeadDuplicate) => {
    const how = translate(`crm.deals.lead.dup.match.${match.match}`, {
      _: MATCH_FALLBACK[match.match],
    });
    return match.match === "name" && match.similarity != null
      ? `${how} ${Math.round(match.similarity * 100)} %`
      : how;
  };
  const used = (match: LeadDuplicate) =>
    match.kind === "contact"
      ? usedContactId != null && String(usedContactId) === String(match.id)
      : usedCompanyId != null && String(usedCompanyId) === String(match.id);

  return (
    <section
      aria-live="polite"
      aria-label={translate("crm.kjenner.title", { _: "Kjenner vi dem?" })}
      data-lead-duplicates=""
      className="rounded-md border bg-muted/30 p-3"
    >
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Search className="h-3.5 w-3.5" />
        {translate("crm.kjenner.title", { _: "Kjenner vi dem?" })}
        {hasInput && !isPending && matches.length === 0 && (
          <span className="font-normal normal-case tracking-normal text-foreground">
            {translate("crm.deals.lead.dup.none", { _: "Nei" })}
          </span>
        )}
        {isPending && (
          <span className="font-normal normal-case tracking-normal">
            {translate("crm.deals.lead.dup.checking", { _: "Sjekker …" })}
          </span>
        )}
      </div>
      {!hasInput ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.deals.lead.dup.hint", {
            _: "Skriv e-post, telefon eller firma, så sjekker vi om vi kjenner dem.",
          })}
        </p>
      ) : (
        <div className="flex flex-col gap-3 text-sm">
          {summary.openDeals.map((deal) => (
            <div
              key={`deal-${deal.id}`}
              role="alert"
              className="flex flex-col gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2"
            >
              <div className="flex items-start gap-2">
                <Handshake className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
                <span className="min-w-0 flex-1">
                  {translate("crm.deals.lead.dup.open_deal", {
                    _: "Finnes allerede: %{title}",
                    title: deal.label,
                  })}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button asChild size="sm" variant="outline" className="h-7">
                  <Link to={`/deals/${deal.id}/show`}>
                    {translate("crm.deals.lead.dup.open", {
                      _: "Åpne muligheten",
                    })}
                  </Link>
                </Button>
                {onAddNote && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-7"
                    onClick={() => onAddNote(deal)}
                  >
                    {translate("crm.deals.lead.dup.add_note", {
                      _: "Legg til notat i stedet",
                    })}
                  </Button>
                )}
              </div>
            </div>
          ))}
          {summary.lostDeals.map((deal) => (
            <div
              key={`lost-${deal.id}`}
              className="flex items-start gap-2 rounded-md border p-2"
            >
              <Handshake className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1">
                {translate("crm.deals.lead.dup.lost_deal", {
                  _: "Tapt mulighet: %{title}.",
                  title: deal.label,
                })}{" "}
                <span className="text-muted-foreground">
                  {translate("crm.deals.lead.dup.lost_hint", {
                    _: "Under 6 uker siden og samme behov: gjenåpne den. Ellers lagrer vi en ny mulighet.",
                  })}
                </span>{" "}
                <Link
                  to={`/deals/${deal.id}/show`}
                  className="underline underline-offset-4"
                >
                  {translate("crm.deals.lead.dup.see_lost", {
                    _: "Se den tapte",
                  })}
                </Link>
              </span>
            </div>
          ))}
          {summary.customer && (
            <p className="flex items-start gap-2">
              <Badge variant="secondary">
                {translate("crm.deals.lead.dup.customer_badge", {
                  _: "Eksisterende kunde",
                })}
              </Badge>
              <span className="text-muted-foreground">
                {translate("crm.deals.lead.dup.customer_hint", {
                  _: "Kilden blir «Eksisterende kunde (mersalg)», og kundeansvarlig blir eier.",
                })}
              </span>
            </p>
          )}
          {[...summary.contacts, ...summary.companies].length > 0 && (
            <ul className="flex flex-col gap-1">
              {[...summary.contacts, ...summary.companies].map((match) => {
                const Icon = match.kind === "contact" ? ContactIcon : Building;
                return (
                  <li
                    key={`${match.kind}-${match.id}`}
                    className="flex items-center gap-2"
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">
                      {match.label}
                      <span className="text-muted-foreground">
                        {" "}
                        · {matchLabel(match)}
                      </span>
                    </span>
                    {onUse &&
                      (used(match) ? (
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {translate("crm.deals.lead.dup.used", {
                            _: "Brukes",
                          })}
                        </span>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 shrink-0"
                          aria-label={translate(
                            "crm.deals.lead.dup.use_named",
                            {
                              _: "Bruk %{name}",
                              name: match.label,
                            },
                          )}
                          onClick={() => onUse(match)}
                        >
                          {translate("crm.deals.lead.dup.use", { _: "Bruk" })}
                        </Button>
                      ))}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};
